import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { signOut } from "../services/authService";
import { getBillingConfig, saveBillingConfig, DEFAULT_CONFIG } from "../services/billingConfigService";
import {
  ArrowLeft,
  User,
  Mic,
  Shield,
  Receipt,
  Database,
  ChevronRight,
  LogOut,
  Volume2,
  Trash2,
  RefreshCw,
  Info,
  AlertTriangle,
} from "lucide-react";
import BottomNav from "../components/BottomNav";

function SettingsSection({ icon: Icon, title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      className="glass-card mb-3 overflow-hidden"
      style={{ cursor: "default" }}
    >
      <button
        className="w-full flex items-center justify-between p-4"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: "rgba(99,179,237,0.08)" }}
          >
            <Icon size={18} className="text-blue-400" />
          </div>
          <span className="text-sm font-semibold text-gray-200">{title}</span>
        </div>
        <ChevronRight
          size={16}
          className="text-gray-600 transition-transform"
          style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
        />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-0 animate-fade-in">
          <div className="h-px mb-3" style={{ background: "rgba(99,179,237,0.06)" }} />
          {children}
        </div>
      )}
    </div>
  );
}

function ToggleSwitch({ value, onChange, label, description }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <div className="flex-1 mr-4">
        <div className="text-sm text-gray-300">{label}</div>
        {description && <div className="text-[11px] text-gray-600 mt-0.5">{description}</div>}
      </div>
      <button
        className="w-11 h-6 rounded-full relative transition-colors shrink-0"
        style={{
          background: value ? "rgba(72,187,120,0.4)" : "rgba(99,179,237,0.1)",
          border: `1px solid ${value ? "rgba(72,187,120,0.5)" : "rgba(99,179,237,0.15)"}`,
        }}
        onClick={() => onChange(!value)}
      >
        <div
          className="w-4.5 h-4.5 rounded-full absolute top-[2px] transition-all"
          style={{
            width: 18,
            height: 18,
            background: value ? "#48bb78" : "#4a5568",
            left: value ? 22 : 3,
          }}
        />
      </button>
    </div>
  );
}

function SelectOption({ label, value, options, onChange }) {
  return (
    <div className="py-2.5">
      <div className="text-sm text-gray-300 mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => (
          <button
            key={opt.value}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
            style={{
              background: value === opt.value ? "rgba(49,130,206,0.2)" : "rgba(15,23,42,0.5)",
              color: value === opt.value ? "#63b3ed" : "#718096",
              border: `1px solid ${value === opt.value ? "rgba(49,130,206,0.3)" : "rgba(99,179,237,0.06)"}`,
            }}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { isOnline } = useOnlineStatus();

  // Profile
  const [agentName] = useState(profile?.displayName || "");
  const [agentCredentials] = useState(profile?.credentials || "ICM");
  const [agentId] = useState(profile?.employeeId || "");

  // Admin Security
  const [adminPinInput, setAdminPinInput] = useState("");
  const [pinSaved, setPinSaved] = useState(false);

  // Voice Commands
  const [voiceTriggersEnabled, setVoiceTriggersEnabled] = useState(true);
  const [customTriggerPhrase, setCustomTriggerPhrase] = useState("");
  const [triggerSensitivity, setTriggerSensitivity] = useState("medium");
  const [hapticEnabled, setHapticEnabled] = useState(true);
  const [audioChimeEnabled, setAudioChimeEnabled] = useState(false);

  // Audio & Privacy
  const [retentionPeriod, setRetentionPeriod] = useState("72h");
  const [autoDeleteAfterSubmit, setAutoDeleteAfterSubmit] = useState(true);
  const [encryptLocal, setEncryptLocal] = useState(true);
  const [autoSaveInterval, setAutoSaveInterval] = useState("10s");

  // Billing Config (org-level, persisted to Firestore)
  const [billingConfig, setBillingConfig] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingSaved, setBillingSaved] = useState(false);

  // Load billing config on mount
  useEffect(() => {
    const org = profile?.organization || localStorage.getItem("fieldnotes_user_org") || "default-org";
    getBillingConfig(org).then(config => {
      setBillingConfig(config);
      setBillingLoading(false);
    });
  }, [profile]);

  const handleSaveBilling = async () => {
    if (!billingConfig) return;
    const org = profile?.organization || localStorage.getItem("fieldnotes_user_org") || "default-org";
    const ok = await saveBillingConfig(org, billingConfig);
    if (ok) {
      setBillingSaved(true);
      // Also cache locally for aiService to read without async
      try { localStorage.setItem("fieldnotes_billing_config", JSON.stringify(billingConfig)); } catch (e) {}
      setTimeout(() => setBillingSaved(false), 2000);
    }
  };

  const updateServiceConfig = (serviceKey, field, value) => {
    setBillingConfig(prev => ({
      ...prev,
      services: {
        ...prev.services,
        [serviceKey]: {
          ...prev.services[serviceKey],
          [field]: value,
        },
      },
    }));
  };

  // Offline
  const [cachedSessions] = useState(2);
  const [localStorageUsed] = useState("14.2 MB");
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  const handleForceSync = () => {
    // In production, trigger sync of all pending data
  };

  const handleClearData = () => {
    // In production, clear IndexedDB and local caches
    setShowClearConfirm(false);
  };

  return (
    <div className="min-h-screen bg-surface-primary">
      <header
        className="sticky top-0 z-50 px-5 py-4 flex items-center justify-between"
        style={{
          background: "linear-gradient(135deg, #0f1729, #162033)",
          borderBottom: "1px solid rgba(99,179,237,0.1)",
        }}
      >
        <button className="flex items-center gap-3" onClick={() => navigate("/dashboard")}>
          <ArrowLeft size={20} className="text-gray-300" />
          <span className="text-lg font-bold text-gray-100">Settings</span>
        </button>
      </header>

      <div className="px-5 py-4 pb-28">
        {/* ===== PROFILE ===== */}
        <SettingsSection icon={User} title="Profile" defaultOpen={true}>
          <div
            className="flex items-center gap-4 p-3 rounded-xl mb-3"
            style={{ background: "rgba(15,23,42,0.5)", border: "1px solid rgba(99,179,237,0.06)" }}
          >
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold text-white shrink-0"
              style={{ background: "linear-gradient(135deg, #3182ce, #2b6cb0)" }}
            >
              {agentName.split(" ").map(n => n[0]).join("").slice(0, 2)}
            </div>
            <div>
              <div className="text-base font-bold text-gray-100">{agentName}</div>
              <div className="text-xs text-gray-500">{agentCredentials} · Employee ID: {agentId}</div>
              <div className="text-xs text-teal-400 mt-0.5">Highland Rivers Health — ICM</div>
            </div>
          </div>
          <div className="text-[11px] text-gray-600">
            Contact your administrator to update profile information.
          </div>
        </SettingsSection>

        {/* ===== VOICE COMMANDS ===== */}
        <SettingsSection icon={Mic} title="Voice Commands & Triggers">
          <ToggleSwitch
            value={voiceTriggersEnabled}
            onChange={setVoiceTriggersEnabled}
            label="Voice Triggers"
            description="Detect trigger phrases during recording"
          />

          <ToggleSwitch
            value={hapticEnabled}
            onChange={setHapticEnabled}
            label="Haptic Feedback"
            description="Vibrate phone when a trigger is detected"
          />

          <ToggleSwitch
            value={audioChimeEnabled}
            onChange={setAudioChimeEnabled}
            label="Audio Confirmation"
            description="Play a subtle chime when a trigger fires"
          />

          <SelectOption
            label="Trigger Sensitivity"
            value={triggerSensitivity}
            onChange={setTriggerSensitivity}
            options={[
              { value: "low", label: "Low — Exact phrases only" },
              { value: "medium", label: "Medium — Fuzzy match" },
              { value: "high", label: "High — Broad match" },
            ]}
          />

          <div className="py-2.5">
            <div className="text-sm text-gray-300 mb-1.5">Add Custom Trigger Phrase</div>
            <div className="flex gap-2">
              <input
                className="field-input flex-1 text-sm"
                style={{ padding: "10px 14px" }}
                placeholder='e.g. "mark this important"'
                value={customTriggerPhrase}
                onChange={(e) => setCustomTriggerPhrase(e.target.value)}
              />
              <button
                className="px-4 rounded-xl text-sm font-semibold shrink-0"
                style={{
                  background: customTriggerPhrase ? "linear-gradient(135deg, #3182ce, #2b6cb0)" : "rgba(99,179,237,0.08)",
                  color: customTriggerPhrase ? "white" : "#4a5568",
                }}
                onClick={() => {
                  if (customTriggerPhrase.trim()) {
                    setCustomTriggerPhrase("");
                  }
                }}
              >
                Add
              </button>
            </div>
          </div>

          {/* Built-in triggers reference */}
          <div className="mt-2">
            <div className="text-[11px] font-bold text-accent-teal tracking-wider uppercase mb-2">Active Triggers</div>
            {[
              { category: "Flag Note", examples: '"take this note", "flag this", "that\'s important", "I should note this"', color: "#ecc94b" },
              { category: "Priority Flag", examples: '"I\'m concerned", "safety concern", "call my supervisor", "DEFCON"', color: "#fc8181" },
              { category: "End Flag", examples: '"end note", "got it", "moving on"', color: "#68d391" },
              { category: "Medication", examples: '"medication check", "taking your meds", "prescription"', color: "#63b3ed" },
              { category: "Safety", examples: '"safety assessment", "safety plan", "suicidal thoughts"', color: "#63b3ed" },
              { category: "Housing", examples: '"housing situation", "where are you living", "eviction"', color: "#63b3ed" },
              { category: "Coping", examples: '"coping skills", "how are you coping", "support groups"', color: "#63b3ed" },
              { category: "End Session", examples: '"end session", "wrap up", "session complete"', color: "#a0aec0" },
            ].map((t, i) => (
              <div
                key={i}
                className="flex gap-2 py-2"
                style={{ borderBottom: i < 7 ? "1px solid rgba(99,179,237,0.04)" : "none" }}
              >
                <span className="text-xs font-semibold shrink-0 w-24" style={{ color: t.color }}>{t.category}</span>
                <span className="text-[11px] text-gray-500 leading-relaxed">{t.examples}</span>
              </div>
            ))}
          </div>
        </SettingsSection>

        {/* ===== AUDIO & PRIVACY ===== */}
        <SettingsSection icon={Shield} title="Audio & Privacy">
          <SelectOption
            label="Audio Retention Period"
            value={retentionPeriod}
            onChange={setRetentionPeriod}
            options={[
              { value: "24h", label: "24 hours" },
              { value: "72h", label: "72 hours" },
              { value: "7d", label: "7 days" },
              { value: "30d", label: "30 days" },
            ]}
          />

          <ToggleSwitch
            value={autoDeleteAfterSubmit}
            onChange={setAutoDeleteAfterSubmit}
            label="Auto-Delete After Submit"
            description="Delete audio recording once the note is submitted"
          />

          <ToggleSwitch
            value={encryptLocal}
            onChange={setEncryptLocal}
            label="Encrypt Local Storage"
            description="AES-256 encryption for all locally cached data"
          />

          <SelectOption
            label="Auto-Save Interval"
            value={autoSaveInterval}
            onChange={setAutoSaveInterval}
            options={[
              { value: "5s", label: "5 seconds" },
              { value: "10s", label: "10 seconds" },
              { value: "30s", label: "30 seconds" },
              { value: "60s", label: "60 seconds" },
            ]}
          />

          <div
            className="flex items-center gap-2.5 p-3 rounded-xl mt-2"
            style={{ background: "rgba(72,187,120,0.04)", border: "1px solid rgba(72,187,120,0.1)" }}
          >
            <Shield size={14} className="text-green-400 shrink-0" />
            <div className="text-[11px] text-gray-500 leading-relaxed">
              All data is encrypted in transit (TLS) and at rest. Audio recordings are stored in HIPAA-compliant cloud storage with BAA coverage.
            </div>
          </div>
        </SettingsSection>

        {/* ===== BILLING DEFAULTS ===== */}
        <SettingsSection icon={Receipt} title="Billing Configuration">
          {billingLoading ? (
            <div className="text-xs text-gray-500 py-4 text-center">Loading billing config...</div>
          ) : billingConfig ? (
            <>
              {/* Code Format */}
              <div className="mb-4">
                <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Code Format on Notes</div>
                <div className="flex gap-2">
                  {[
                    { value: "hcpcs", label: "HCPCS (H0039)" },
                    { value: "dbhdd", label: "DBHDD (ACT)" },
                    { value: "both", label: "Both" },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      className="flex-1 py-2 rounded-lg text-xs font-semibold"
                      style={{
                        background: billingConfig.codeFormat === opt.value ? "rgba(49,130,206,0.2)" : "rgba(15,23,42,0.5)",
                        color: billingConfig.codeFormat === opt.value ? "#63b3ed" : "#718096",
                        border: `1px solid ${billingConfig.codeFormat === opt.value ? "rgba(49,130,206,0.3)" : "rgba(99,179,237,0.06)"}`,
                      }}
                      onClick={() => setBillingConfig(prev => ({ ...prev, codeFormat: opt.value }))}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Unit Duration */}
              <ToggleSwitch
                value={billingConfig.autoCalculateUnits}
                onChange={(v) => setBillingConfig(prev => ({ ...prev, autoCalculateUnits: v }))}
                label="Auto-Calculate Units"
                description={`Calculate billing units from session duration (${billingConfig.unitMinutes || 15}-min increments)`}
              />

              {/* Enabled Services */}
              <div className="mt-4 mb-2">
                <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Enabled Services</div>
                <div className="text-[10px] text-gray-600 mb-3">Toggle on the services your agency provides. Agents will only see enabled services when starting a session.</div>
              </div>

              {Object.entries(billingConfig.services).map(([key, svc]) => (
                <div
                  key={key}
                  className="rounded-xl p-3 mb-2"
                  style={{
                    background: svc.enabled ? "rgba(49,130,206,0.04)" : "rgba(15,23,42,0.3)",
                    border: `1px solid ${svc.enabled ? "rgba(49,130,206,0.12)" : "rgba(99,179,237,0.04)"}`,
                    opacity: svc.enabled ? 1 : 0.6,
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <button
                        className="w-5 h-5 rounded flex items-center justify-center text-xs"
                        style={{
                          background: svc.enabled ? "rgba(72,187,120,0.3)" : "rgba(99,179,237,0.08)",
                          color: svc.enabled ? "#48bb78" : "#718096",
                          border: `1px solid ${svc.enabled ? "rgba(72,187,120,0.4)" : "rgba(99,179,237,0.1)"}`,
                        }}
                        onClick={() => updateServiceConfig(key, "enabled", !svc.enabled)}
                      >
                        {svc.enabled ? "✓" : ""}
                      </button>
                      <span className="text-sm font-semibold text-gray-200">{key}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-blue-400">{svc.hcpcsCode}</span>
                      <span className="text-[10px] text-gray-600">/</span>
                      <span className="text-[10px] font-mono text-teal-400">{svc.dbhddCode}</span>
                    </div>
                  </div>

                  {svc.enabled && (
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-gray-500 block mb-1">HCPCS Code</label>
                        <input
                          type="text"
                          value={svc.hcpcsCode}
                          onChange={(e) => updateServiceConfig(key, "hcpcsCode", e.target.value.toUpperCase())}
                          className="w-full rounded-lg px-2 py-1.5 text-xs font-mono"
                          style={{
                            background: "rgba(15,23,42,0.5)",
                            border: "1px solid rgba(99,179,237,0.1)",
                            color: "#e2e8f0",
                          }}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500 block mb-1">DBHDD Code</label>
                        <input
                          type="text"
                          value={svc.dbhddCode}
                          onChange={(e) => updateServiceConfig(key, "dbhddCode", e.target.value.toUpperCase())}
                          className="w-full rounded-lg px-2 py-1.5 text-xs font-mono"
                          style={{
                            background: "rgba(15,23,42,0.5)",
                            border: "1px solid rgba(99,179,237,0.1)",
                            color: "#e2e8f0",
                          }}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500 block mb-1">Staff Title</label>
                        <input
                          type="text"
                          value={svc.staffTitle}
                          onChange={(e) => updateServiceConfig(key, "staffTitle", e.target.value)}
                          className="w-full rounded-lg px-2 py-1.5 text-xs"
                          style={{
                            background: "rgba(15,23,42,0.5)",
                            border: "1px solid rgba(99,179,237,0.1)",
                            color: "#e2e8f0",
                          }}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500 block mb-1">Max Daily Units</label>
                        <input
                          type="number"
                          value={svc.maxDailyUnits}
                          onChange={(e) => updateServiceConfig(key, "maxDailyUnits", parseInt(e.target.value) || 1)}
                          className="w-full rounded-lg px-2 py-1.5 text-xs"
                          style={{
                            background: "rgba(15,23,42,0.5)",
                            border: "1px solid rgba(99,179,237,0.1)",
                            color: "#e2e8f0",
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Save Button */}
              <button
                onClick={handleSaveBilling}
                className="w-full mt-3 py-2.5 rounded-xl text-xs font-bold"
                style={{
                  background: billingSaved ? "rgba(72,187,120,0.2)" : "linear-gradient(135deg, #3182ce, #2b6cb0)",
                  color: billingSaved ? "#48bb78" : "white",
                  border: billingSaved ? "1px solid rgba(72,187,120,0.3)" : "none",
                }}
              >
                {billingSaved ? "✓ Billing Config Saved" : "Save Billing Configuration"}
              </button>
              <div className="text-[10px] text-gray-600 mt-2 text-center">
                This config applies to all agents in your organization.
              </div>
            </>
          ) : (
            <div className="text-xs text-red-400 py-4 text-center">Failed to load billing config</div>
          )}
        </SettingsSection>

        {/* ===== OFFLINE & DATA ===== */}
        <SettingsSection icon={Database} title="Offline & Data Management">
          <div className="flex justify-between items-center py-2.5">
            <div>
              <div className="text-sm text-gray-300">Cached Sessions</div>
              <div className="text-[11px] text-gray-600 mt-0.5">Sessions stored locally for offline access</div>
            </div>
            <span className="text-sm font-bold text-yellow-400">{cachedSessions}</span>
          </div>

          <div className="flex justify-between items-center py-2.5">
            <div>
              <div className="text-sm text-gray-300">Local Storage Used</div>
              <div className="text-[11px] text-gray-600 mt-0.5">Audio, transcripts, and cached data</div>
            </div>
            <span className="text-sm font-bold text-blue-400">{localStorageUsed}</span>
          </div>

          <div className="flex justify-between items-center py-2.5">
            <div>
              <div className="text-sm text-gray-300">Connection Status</div>
            </div>
            <span
              className="text-sm font-bold"
              style={{ color: isOnline ? "#48bb78" : "#fc8181" }}
            >
              {isOnline ? "Online" : "Offline"}
            </span>
          </div>

          <button
            className="w-full flex items-center justify-between py-3 rounded-xl text-sm font-semibold mt-3"
            style={{
              background: "rgba(236,201,75,0.06)",
              color: "#ecc94b",
              border: "1px solid rgba(236,201,75,0.2)",
              padding: "12px 16px",
            }}
            onClick={() => navigate("/recovery")}
          >
            <span className="flex items-center gap-2">
              <AlertTriangle size={14} /> Recover Lost Sessions
            </span>
            <ChevronRight size={14} />
          </button>
          <div className="text-[10px] text-gray-600 mt-1 px-1 leading-relaxed">
            Surfaces audio recordings still in this device's storage that never made it to a submitted note.
          </div>

          <div className="flex gap-2 mt-3">
            <button
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold"
              style={{
                background: "rgba(99,179,237,0.08)",
                color: "#63b3ed",
                border: "1px solid rgba(99,179,237,0.2)",
              }}
              onClick={handleForceSync}
            >
              <RefreshCw size={14} /> Force Sync
            </button>
            <button
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold"
              style={{
                background: "rgba(252,129,129,0.06)",
                color: "#fc8181",
                border: "1px solid rgba(252,129,129,0.15)",
              }}
              onClick={() => setShowClearConfirm(true)}
            >
              <Trash2 size={14} /> Clear Cache
            </button>
          </div>
        </SettingsSection>

        {/* ===== ADMIN SECURITY ===== */}
        {(profile?.role === "admin" || profile?.role === "supervisor") && (
          <SettingsSection icon={Shield} title="Admin Security">
            <div className="py-2">
              <div className="text-sm text-gray-300 mb-1">Admin PIN</div>
              <div className="text-[11px] text-gray-600 mb-3">
                Used to confirm sensitive actions like deactivating individuals. Default: 1234
              </div>
              <div className="flex gap-2">
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder={localStorage.getItem("fieldnotes_admin_pin") ? "••••" : "1234 (default)"}
                  value={adminPinInput}
                  onChange={e => setAdminPinInput(e.target.value.replace(/\D/g, ""))}
                  className="flex-1 px-3 py-2.5 rounded-lg text-sm text-center font-mono tracking-widest"
                  style={{
                    background: "rgba(15,23,42,0.5)",
                    border: "1px solid rgba(99,179,237,0.08)",
                    color: "#e2e8f0",
                    outline: "none",
                    letterSpacing: 6,
                  }}
                />
                <button
                  className="px-4 py-2.5 rounded-lg text-xs font-bold"
                  style={{
                    background: adminPinInput.length >= 4
                      ? "linear-gradient(135deg, #3182ce, #2b6cb0)"
                      : "rgba(49,130,206,0.2)",
                    color: adminPinInput.length >= 4 ? "white" : "#4a5568",
                    border: "none",
                    cursor: adminPinInput.length >= 4 ? "pointer" : "default",
                  }}
                  disabled={adminPinInput.length < 4}
                  onClick={() => {
                    localStorage.setItem("fieldnotes_admin_pin", adminPinInput);
                    setAdminPinInput("");
                    setPinSaved(true);
                    setTimeout(() => setPinSaved(false), 2000);
                  }}
                >
                  {pinSaved ? "Saved!" : "Set PIN"}
                </button>
              </div>
              {pinSaved && (
                <div className="text-[11px] text-green-400 mt-2 font-semibold">PIN updated successfully</div>
              )}
            </div>

            <div className="py-2 mt-2 border-t" style={{ borderColor: "rgba(99,179,237,0.06)" }}>
              <div className="text-sm text-gray-300 mb-1">Clear Old Safety Alerts</div>
              <div className="text-[11px] text-gray-600 mb-3">
                Resolve all active safety alerts at once. Use this to clean up false positives from testing.
              </div>
              <button
                className="px-4 py-2.5 rounded-lg text-xs font-bold"
                style={{
                  background: "rgba(246,173,85,0.08)",
                  color: "#f6ad55",
                  border: "1px solid rgba(246,173,85,0.15)",
                  cursor: "pointer",
                }}
                onClick={async () => {
                  if (!window.confirm("Resolve ALL active safety alerts? This marks them as resolved, not deleted.")) return;
                  try {
                    const { collection, query, where, getDocs, updateDoc, serverTimestamp } = await import("firebase/firestore");
                    const { orgCollection } = await import("../services/orgPaths");
                    const q = query(
                      orgCollection("safetyAlerts"),
                      where("status", "==", "active")
                    );
                    const snap = await getDocs(q);
                    let count = 0;
                    for (const d of snap.docs) {
                      await updateDoc(d.ref, {
                        status: "resolved",
                        resolvedBy: profile?.displayName || user?.email || "Admin",
                        resolvedAt: serverTimestamp(),
                        resolutionNotes: "Bulk resolved from Settings (cleanup)",
                      });
                      count++;
                    }
                    alert(`Resolved ${count} active alert(s).`);
                  } catch (e) {
                    alert("Error: " + e.message);
                  }
                }}
              >
                Resolve All Active Alerts
              </button>
            </div>
          </SettingsSection>
        )}

        {/* ===== SYNC TO CLOUD ===== */}
        <button
          className="glass-card p-4 mb-3 w-full flex items-center justify-between"
          onClick={() => {
            if (window.confirm("Sync all your local data (notes, sessions, individuals) to the cloud so your supervisor can see it?")) {
              const { syncAllToFirestore } = require("../hooks/useLocalData");
              // Use the context version instead
              import("../services/firestoreSync").then(mod => {
                const stored = localStorage.getItem("fieldnotes_local_data");
                if (stored) {
                  mod.syncAllLocalData(JSON.parse(stored));
                  alert("Sync started! Your data is being uploaded.");
                }
              });
            }
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: "rgba(79,209,197,0.08)" }}
            >
              <RefreshCw size={18} className="text-teal-400" />
            </div>
            <div className="text-left">
              <span className="text-sm font-semibold text-gray-200">Sync to Cloud</span>
              <div className="text-[11px] text-gray-600">Push all local data so your supervisor can see it</div>
            </div>
          </div>
          <div className="text-xs text-teal-400 font-semibold">Sync</div>
        </button>

        {/* ===== TRASH ===== */}
        <button
          className="glass-card p-4 mb-3 w-full flex items-center justify-between"
          onClick={() => navigate("/trash")}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: "rgba(236,201,75,0.08)" }}
            >
              <Trash2 size={18} className="text-yellow-400" />
            </div>
            <div className="text-left">
              <span className="text-sm font-semibold text-gray-200">Trash</span>
              <div className="text-[11px] text-gray-600">Deleted items are kept for 30 days</div>
            </div>
          </div>
          <div className="text-xs text-gray-500 font-semibold">View</div>
        </button>

        {/* ===== ADMIN DASHBOARD ===== */}
        <button
          className="glass-card p-4 mb-3 flex items-center justify-between w-full"
          onClick={() => navigate("/admin")}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: "rgba(183,148,244,0.08)" }}
            >
              <Shield size={18} className="text-purple-400" />
            </div>
            <div className="text-left">
              <span className="text-sm font-semibold text-gray-200">Supervisor Dashboard</span>
              <div className="text-[11px] text-gray-600">Team overview, safety alerts, note review</div>
            </div>
          </div>
          <div className="text-xs text-purple-400 font-semibold">Open</div>
        </button>

        {/* ===== ABOUT ===== */}
        <div className="glass-card p-4 mb-3">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: "rgba(99,179,237,0.08)" }}
            >
              <Info size={18} className="text-blue-400" />
            </div>
            <span className="text-sm font-semibold text-gray-200">About FieldNotes</span>
          </div>
          <div className="text-xs text-gray-500 leading-relaxed">
            Version 0.1.0 — Phase 1 Beta
          </div>
          <div className="text-xs text-gray-600 mt-1">
            AI-Powered Clinical Documentation for ICM Field Agents
          </div>
          <div className="text-xs text-gray-600 mt-0.5">
            Highland Rivers Health · Georgia DBHDD Compliant
          </div>
          <div className="text-[10px] text-gray-700 mt-2">
            HIPAA Compliant · AES-256 Encryption · Firebase + Anthropic Claude
          </div>
        </div>

        {/* ===== LOGOUT ===== */}
        <button
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-semibold mt-2"
          style={{
            background: "rgba(252,129,129,0.06)",
            color: "#fc8181",
            border: "1px solid rgba(252,129,129,0.15)",
          }}
          onClick={() => setShowLogoutConfirm(true)}
        >
          <LogOut size={16} /> Sign Out
        </button>

        <div className="text-center text-[10px] text-gray-700 mt-4">
          Session timeout: 30 minutes of inactivity
        </div>
      </div>

      {/* Clear Cache Confirm */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 px-8">
          <div
            className="w-full max-w-sm rounded-2xl p-6"
            style={{
              background: "linear-gradient(135deg, #0f1729, #162033)",
              border: "1px solid rgba(252,129,129,0.2)",
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={18} className="text-red-400" />
              <h3 className="text-lg font-bold text-gray-100">Clear Local Data?</h3>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-5">
              This will delete all cached audio recordings, transcripts, and offline session data from this device. Submitted notes in the cloud will not be affected. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                className="flex-1 py-3 rounded-xl text-sm font-semibold"
                style={{ background: "rgba(99,179,237,0.08)", color: "#63b3ed", border: "1px solid rgba(99,179,237,0.2)" }}
                onClick={() => setShowClearConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white"
                style={{ background: "linear-gradient(135deg, #dc2626, #b91c1c)" }}
                onClick={handleClearData}
              >
                Clear Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirm */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 px-8">
          <div
            className="w-full max-w-sm rounded-2xl p-6"
            style={{
              background: "linear-gradient(135deg, #0f1729, #162033)",
              border: "1px solid rgba(99,179,237,0.15)",
            }}
          >
            <h3 className="text-lg font-bold text-gray-100 mb-2">Sign Out?</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-5">
              Any unsaved draft sessions on this device will be preserved for when you sign back in.
            </p>
            <div className="flex gap-3">
              <button
                className="flex-1 py-3 rounded-xl text-sm font-semibold"
                style={{ background: "rgba(99,179,237,0.08)", color: "#63b3ed", border: "1px solid rgba(99,179,237,0.2)" }}
                onClick={() => setShowLogoutConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white"
                style={{ background: "linear-gradient(135deg, #dc2626, #b91c1c)" }}
                onClick={handleLogout}
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav active="settings" />
    </div>
  );
}